import { useEffect, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getCardAction } from 'src/reducers/card/action'
import { useLocation } from 'react-router-dom'
import { isAnswerItemTypeResultCard } from 'src/model/card'
import { SectionTypes } from 'src/model/card'
import { useParams } from 'react-router-dom'

// Move this outside the hook since it's a pure function
const getEndingCard = course => {
  if (!course?.sections) return null

  // Find ending section
  const endingSection = course.sections.find(
    section => section.type === SectionTypes.ENDING,
  )

  if (endingSection?.cards?.[0]) {
    return endingSection.cards[0]
  }

  // Check all cards for ending card
  return course.sections.reduce((found, section) => {
    if (found) return found
    return section.cards.find(card => isAnswerItemTypeResultCard(card.type))
  }, null)
}

const useCardData = () => {
  const dispatch = useDispatch()
  const [isEndingCardExist, setIsEndingCardExist] = useState(false)
  const [shouldScroll, setShouldScroll] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const cardData = useSelector(state => state.card.currentCard)
  const course = useSelector(state => state.course.course)
  const endingCard = useSelector(state => state.course.endingCard)
  const { id, page, subPage } = useParams()

  const handleCardRequest = useCallback(() => {
    if (page === 'cover') {
      setIsLoading(false)
      return
    }

    if (page === 'landing-page') {
      setIsLoading(false)
      return
    }
    if (page === 'page' && subPage) {
      setIsLoading(true)
      dispatch(getCardAction(subPage))
      setShouldScroll(true)
      return
    }
    if (page === 'page' && !subPage) {
      setIsLoading(false)
      return
    }

    if (page === 'completion') {
      if (endingCard) {
        setIsEndingCardExist(true)
        setIsLoading(true)
        dispatch(getCardAction(endingCard.id))
        setShouldScroll(true)
        return
      }

      const foundEndingCard = getEndingCard(course)
      if (foundEndingCard) {
        setIsEndingCardExist(true)
        setIsLoading(true)
        dispatch(getCardAction(foundEndingCard.id))
        setShouldScroll(true)
        return
      }

      setIsEndingCardExist(false)
      setIsLoading(false)
      return
    }

    if (subPage) {
      setIsLoading(true)
      dispatch(getCardAction(subPage))
      setShouldScroll(true)
    }
  }, [page, dispatch, course, endingCard, subPage])

  // Add conditions to prevent unnecessary effect triggers
  useEffect(() => {
    // Skip effect if we don't have necessary data
    if (!page || (page === 'completion' && !course)) {
      setIsLoading(false)
      return
    }

    handleCardRequest()
  }, [subPage, page, endingCard])

  // Force scroll on URL changes with same page
  useEffect(() => {
    if (page && cardData?.id) {
      setShouldScroll(true)
    }
  }, [subPage])

  // Reset loading state when card data changes
  useEffect(() => {
    if (cardData?.id) {
      setIsLoading(false)
    }
  }, [cardData])

  return {
    cardData,
    page,
    course,
    isEndingCardExist,
    isLoading,
  }
}

export default useCardData
